/**
 * Web HTTP Client for browser-based API calls
 * Supports both Auth0 and API key authentication
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { sessionStorage } from './sessionStorage'
import { getTokenGetter } from '../../hooks/use-auth'

// Rate-limit (429) retry tuning
const MAX_RETRIES_429 = 6
const BASE_BACKOFF_MS = 500
const MAX_BACKOFF_MS = 20000

// Client-side throttle to avoid tripping the API rate limiter under burst load.
// Caps the number of in-flight requests and enforces a minimum spacing between starts.
const MAX_CONCURRENT_REQUESTS = 4
const MIN_REQUEST_SPACING_MS = 60

// Per-request retry bookkeeping, stored on the axios request config
interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryCount429?: number
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Lightweight gate that limits concurrent in-flight requests and spaces out
 * request starts. Browsers mask cross-origin 429 responses as opaque network
 * errors (the CORS headers are absent on the error response), so the most
 * reliable defense is to avoid bursting past the limiter in the first place.
 */
class RequestThrottle {
  private active = 0
  private lastStart = 0
  private queue: Array<() => void> = []

  constructor(
    private readonly maxConcurrent: number,
    private readonly minSpacingMs: number
  ) {}

  async acquire(): Promise<void> {
    if (this.active >= this.maxConcurrent) {
      await new Promise<void>((resolve) => this.queue.push(resolve))
    }
    this.active++
    const wait = this.minSpacingMs - (Date.now() - this.lastStart)
    if (wait > 0) await sleep(wait)
    this.lastStart = Date.now()
  }

  release(): void {
    this.active--
    const next = this.queue.shift()
    if (next) next()
  }
}

const isOpaqueNetworkError = (error: AxiosError): boolean =>
  !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')

/**
 * Parse a Retry-After header into milliseconds.
 * Supports both the delta-seconds form ("5") and the HTTP-date form.
 * Returns null if absent or unparseable.
 */
function parseRetryAfter(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  const seconds = Number(value)
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000)
  const dateMs = Date.parse(value)
  if (!Number.isNaN(dateMs)) return Math.max(0, dateMs - Date.now())
  return null
}

export class WebHttpClient {
  private client: AxiosInstance
  private interceptorsSetup = false
  private throttle = new RequestThrottle(MAX_CONCURRENT_REQUESTS, MIN_REQUEST_SPACING_MS)

  constructor() {
    this.client = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors(): void {
    // Prevent duplicate interceptor registration
    if (this.interceptorsSetup) return
    this.interceptorsSetup = true

    // Request interceptor - Add auth header and base URL
    this.client.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Set base URL from current environment
        config.baseURL = sessionStorage.getBaseURL()

        // Try API key authentication first (check for actual credentials)
        const session = sessionStorage.get()

        if (session?.apiKey) {
          // API key is available - use it
          const prefix = session.apiType === 'bearer' ? 'Bearer' : 'Token'
          config.headers.Authorization = `${prefix} ${session.apiKey}`
          console.log('[WebHttpClient] API key injected')
        } else {
          // No API key - try Auth0
          const tokenGetter = getTokenGetter()
          if (tokenGetter) {
            try {
              const token = await tokenGetter()
              if (token) {
                config.headers.Authorization = `Bearer ${token}`
                console.log('[WebHttpClient] Auth0 token injected')
              } else {
                console.warn('[WebHttpClient] ⚠️ No Auth0 token available')
              }
            } catch (error) {
              console.error('[WebHttpClient] Failed to get Auth0 token:', error)
            }
          } else {
            console.warn('[WebHttpClient] ⚠️ No credentials available (no API key or Auth0 token getter)')
          }
        }

        console.log(`[WebHttpClient] 📤 ${config.method?.toUpperCase()} ${config.baseURL}${config.url}`)
        console.log('[WebHttpClient] Authorization header:', config.headers.Authorization ? 'Present' : 'MISSING')

        // Throttle: wait for an available slot before the request goes out
        await this.throttle.acquire()

        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor - Handle errors and log responses
    this.client.interceptors.response.use(
      (response) => {
        // Release throttle slot on success
        this.throttle.release()
        // Log successful responses
        console.log(`[WebHttpClient] ✅ ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`)
        console.log('[WebHttpClient] Response data:', response.data)
        return response
      },
      (error: AxiosError) => {
        // Release the throttle slot this request was holding before doing anything else
        this.throttle.release()

        // Rate limited — retry with exponential backoff (honoring Retry-After if present).
        // Browsers mask cross-origin 429s as opaque network errors (CORS headers are
        // stripped from the error response), so treat those the same as an explicit 429.
        const isRateLimited = error.response?.status === 429 || isOpaqueNetworkError(error)
        if (isRateLimited && error.config) {
          const config = error.config as RetryableConfig
          const attempt = config._retryCount429 ?? 0
          if (attempt < MAX_RETRIES_429) {
            config._retryCount429 = attempt + 1
            const retryAfterMs = parseRetryAfter(error.response?.headers?.['retry-after'])
            // Exponential backoff with full jitter, capped
            const backoff = Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS)
            const jittered = Math.random() * backoff
            const delay = retryAfterMs != null ? Math.max(retryAfterMs, jittered) : jittered
            const reason = error.response?.status === 429 ? '429' : 'opaque network error (likely CORS-masked 429)'
            console.warn(
              `[WebHttpClient] ⏳ rate limited (${reason}) on ${config.method?.toUpperCase()} ${config.url} — retry ${config._retryCount429}/${MAX_RETRIES_429} in ${Math.round(delay)}ms`
            )
            // Slot already released above; the retried request re-acquires via the request interceptor
            return sleep(delay).then(() => this.client.request(config))
          }
          console.error(
            `[WebHttpClient] ❌ rate limited on ${config.method?.toUpperCase()} ${config.url} — exhausted ${MAX_RETRIES_429} retries`
          )
        }

        // Log error responses
        console.error('[WebHttpClient] ❌ Error Response:', {
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method?.toUpperCase(),
          data: error.response?.data,
          message: error.message,
        })

        if (error.response) {
          const status = error.response.status
          const data = error.response.data as any

          switch (status) {
            case 401:
              return Promise.reject({
                code: 'UNAUTHORIZED',
                message: 'Invalid or expired API key',
                details: data,
              })
            case 403:
              return Promise.reject({
                code: 'FORBIDDEN',
                message: 'Insufficient permissions',
                details: data,
              })
            case 404:
              return Promise.reject({
                code: 'NOT_FOUND',
                message: 'Resource not found',
                details: data,
              })
            case 422:
              return Promise.reject({
                code: 'VALIDATION_ERROR',
                message: data?.details?.[0] || data?.message || 'Invalid request data',
                details: data,
              })
            case 429:
              return Promise.reject({
                code: 'RATE_LIMITED',
                message: 'Too many requests, please try again later',
                details: data,
              })
            case 500:
            case 502:
            case 503:
              return Promise.reject({
                code: 'SERVER_ERROR',
                message: 'Server error, please try again',
                details: data,
              })
            default:
              return Promise.reject({
                code: 'UNKNOWN_ERROR',
                message: data?.message || 'An unexpected error occurred',
                details: data,
              })
          }
        } else if (error.request) {
          return Promise.reject({
            code: 'NETWORK_ERROR',
            message: 'Network error, check your connection',
            details: error.message,
          })
        } else {
          return Promise.reject({
            code: 'REQUEST_ERROR',
            message: error.message,
            details: error,
          })
        }
      }
    )
  }

  /**
   * Get the underlying Axios instance
   */
  getClient(): AxiosInstance {
    return this.client
  }

  /**
   * Test API connection with current credentials
   */
  async testConnection(): Promise<boolean> {
    try {
      await this.client.get('/pools')
      return true
    } catch (error) {
      return false
    }
  }
}

// Export singleton instance
export const httpClient = new WebHttpClient()
